import * as React from 'react';
import { useState, useMemo } from 'react';
import {
  Box,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  Skeleton,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useLocation, useNavigate, useParams } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';

import { getIdFromUrl } from '../../../utils/getIdFromUrl';
import ExtractionBatchExtractionListRow from './ExtractionBatchExtractionListRow';
import DocumentDataEditStatus from './DocumentDataEditStatus';
import useGetAllDocuments from '../../hooks/useGetAllDocuments';
import useGetAllExtractions from '../../hooks/useGetAllExtractions';

import RefreshButton from '../../common/Buttons/RefreshButton';

import DocumentTableBulkStatusUpdate from '../../documents/DocumentTableBulkStatusUpdate';

import TableButtonsHeader from '../../common/TableButtonsHeader';
import { compactTableStyles } from '../../common/tableStyles';

import type { IExtraction } from '../../../types/my-types';

const DividerStyled = styled(Divider)(() => ({
  height: '10px',
  position: 'relative',
  margin: '5px 4px 0',
}));

const TypographyStyled = styled(Typography)({
  fontSize: '12px',
});

function ExtractionBatchDocumentList() {
  const navigate = useNavigate();

  const { dpid, id: eid } = useParams<{ dpid: string; id: string }>();
  const client = useQueryClient();

  const [showFailedOnly, setShowFailedOnly] = useState(false);

  const {
    data: documents,
    isLoading: isLoadingDocuments,
    isFetching: isFetchingDocuments,
    currentPage: currentPageDocuments,
    totalPages: totalPagesDocuments,
    refetch: refetchDocuments,
  } = useGetAllDocuments({
    group: 'extract',
    groupId: eid,
    dpid,
  });

  const {
    data: extractions,
    isLoading: isLoadingExtractions,
    isFetching: isFetchingExtractions,
    currentPage: currentPageExtractions,
    totalPages: totalPagesExtractions,
    ebSummaryFailed,
    ebSummaryInProgress,
    ebSummarySuccessful,
    totalExtractionsCount,
    failedExtractions,
    failedDataProcessingStatus,
    refetch: refetchExtractions,
  } = useGetAllExtractions(eid, dpid);

  // Combine failed extractions and failed data processing status
  const failedCombined = useMemo(() => {
    const list: IExtraction[] = (failedExtractions || []).concat(failedDataProcessingStatus || []);
    const map = new Map<IExtraction['id'], IExtraction>();
    for (const item of list) {
      if (item && item.id != null && !map.has(item.id)) {
        map.set(item.id, item);
      }
    }
    return Array.from(map.values());
  }, [failedExtractions, failedDataProcessingStatus]);

  const isFetching = isFetchingDocuments || isFetchingExtractions;
  const isLoading = isLoadingDocuments || isLoadingExtractions;

  const isStillFetchingExtractions =
    (currentPageExtractions !== 0 &&
      totalPagesExtractions !== 0 &&
      totalPagesExtractions !== currentPageExtractions) ||
    isFetchingExtractions ||
    isLoadingExtractions;

  const isStillFetchingDocuments =
    (currentPageDocuments !== 0 &&
      totalPagesDocuments !== 0 &&
      totalPagesDocuments !== currentPageDocuments) ||
    isFetchingDocuments ||
    isLoadingDocuments;

  const { search } = useLocation();
  const searchParams = new URLSearchParams(search);
  const selectedIds = searchParams.get('selectedIds')?.split(',') || [];

  const visibleExtractions: IExtraction[] = showFailedOnly
    ? failedCombined
    : (extractions as IExtraction[] | undefined) || [];
  const visibleExtractionIds = visibleExtractions.map((e) => e.id);
  const allVisibleSelected =
    visibleExtractionIds.length > 0 &&
    visibleExtractionIds.every((id) => selectedIds.includes(id));

  const handleToggleSelectAll = () => {
    const params = new URLSearchParams(search);
    if (allVisibleSelected) {
      const remaining = selectedIds.filter((id) => !visibleExtractionIds.includes(id));
      if (remaining.length === 0) {
        params.delete('selectedIds');
      } else {
        params.set('selectedIds', remaining.join(','));
      }
    } else {
      const merged = Array.from(new Set([...selectedIds, ...visibleExtractionIds]));
      params.set('selectedIds', merged.join(','));
    }
    navigate({ search: params.toString() });
  };

  const selectedDocumentIds = useMemo(() => {
    if (!extractions || !documents || selectedIds.length === 0) return [];
    const docIds: string[] = [];
    for (const eid of selectedIds) {
      const extraction = (extractions as IExtraction[]).find((e) => e.id === eid);
      if (!extraction) continue;
      const doc = documents.find(
        (d) => getIdFromUrl(d.url) === getIdFromUrl(extraction.document)
      );
      if (doc && !docIds.includes(doc.id)) docIds.push(doc.id);
    }
    return docIds;
  }, [selectedIds, extractions, documents]);

  if (!dpid || !eid) return null;
  return (
    <>
      <TableButtonsHeader>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <TypographyStyled>
            Total:{' '}
            <strong>
              {isStillFetchingExtractions || isStillFetchingDocuments ? (
                <CircularProgress size={10} />
              ) : (
                totalExtractionsCount
              )}
            </strong>
          </TypographyStyled>
          <DividerStyled orientation="vertical" flexItem />
          <TypographyStyled>
            In Progress:{' '}
            <strong>
              {isStillFetchingExtractions || isStillFetchingDocuments ? (
                <CircularProgress size={10} />
              ) : (
                ebSummaryInProgress
              )}
            </strong>
          </TypographyStyled>
          <DividerStyled orientation="vertical" flexItem />
          <TypographyStyled>
            Completed:{' '}
            <strong>
              {isStillFetchingExtractions || isStillFetchingDocuments ? (
                <CircularProgress size={10} />
              ) : (
                <span style={{ color: 'green' }}>{ebSummarySuccessful}</span>
              )}
            </strong>
          </TypographyStyled>
          <DividerStyled orientation="vertical" flexItem />

          <TypographyStyled>
            Failed:{' '}
            <strong>
              {isStillFetchingExtractions || isStillFetchingDocuments ? (
                <CircularProgress size={10} />
              ) : (
                <span
                  style={{
                    color: ebSummaryFailed > 0 ? 'red' : 'inherit',
                  }}
                >
                  {ebSummaryFailed}
                </span>
              )}
            </strong>
          </TypographyStyled>
        </div>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          {failedCombined.length > 0 ? (
            <>
              <FormControlLabel
                control={
                  <Switch
                    checked={showFailedOnly}
                    onChange={(e) => setShowFailedOnly(e.target.checked)}
                    size="small"
                    color="primary"
                  />
                }
                label={<Typography style={{ fontSize: '12px' }}>Failed Only</Typography>}
              />
              {/* <DividerStyled orientation="vertical" flexItem style={{ height: '20px' }} /> */}
            </>
          ) : null}

          {selectedIds.length > 0 ? (
            <Box sx={{ mr: '10px' }}>
              <DocumentTableBulkStatusUpdate
                documents={[]}
                selectedDocumentIds={selectedDocumentIds}
                onSuccess={() => {
                  refetchDocuments();
                  refetchExtractions();
                  client.invalidateQueries({ queryKey: ['extractionBatch'] });
                }}
              />
            </Box>
          ) : null}
          <RefreshButton
            cypressId="btn-eb-details-refresh"
            disabled={isLoading || isFetching}
            isLoading={isLoading || isFetching}
            onClick={() => {
              refetchDocuments();
              refetchExtractions();
              client.invalidateQueries({
                queryKey: ['extractionBatch']
              });
            }}
          />
        </div>
      </TableButtonsHeader>
      <Box sx={compactTableStyles.containerSx}>
        <Table sx={compactTableStyles.tableSx}>
          <TableHead sx={compactTableStyles.tableHeadSx}>
            <TableRow>
              <TableCell align="center" sx={{ width: '40px', px: '4px !important', py: '0 !important' }}>
                <Checkbox
                  size="small"
                  inputProps={
                    {
                      'aria-label': 'select all extractions',
                      'data-cy': 'checkbox-eb-details-all-rows',
                    } as React.InputHTMLAttributes<HTMLInputElement>
                  }
                  checked={allVisibleSelected}
                  onChange={handleToggleSelectAll}
                  disabled={visibleExtractionIds.length === 0}
                  sx={{ color: '#65BBA9', '&.Mui-checked': { color: '#65BBA9' } }}
                />
              </TableCell>
              <TableCell align="center">ID</TableCell>
              <TableCell align="center">Extraction</TableCell>
              <TableCell align="center">Transformation</TableCell>
              <TableCell align="center">Document Data Status</TableCell>
              <TableCell align="center">Document</TableCell>
              <TableCell align="center">Uploaded At</TableCell>
              <TableCell align="center">Finished At</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody data-cy="extraction-batch-table-body" sx={compactTableStyles.tableBodySx}>
            {isLoading
              ? Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell align="center" sx={{ width: '40px', px: '4px !important' }} />
                    <TableCell align="center"><Skeleton animation="wave" variant="rounded" height={14} width={56} sx={{ borderRadius: '4px', mx: 'auto' }} /></TableCell>
                    <TableCell align="center"><Skeleton animation="wave" variant="rounded" height={14} width={52} sx={{ borderRadius: '4px', mx: 'auto' }} /></TableCell>
                    <TableCell align="center"><Skeleton animation="wave" variant="rounded" height={14} width={52} sx={{ borderRadius: '4px', mx: 'auto' }} /></TableCell>
                    <TableCell align="center"><Skeleton animation="wave" variant="rounded" height={22} width={80} sx={{ borderRadius: '6px', mx: 'auto' }} /></TableCell>
                    <TableCell align="center"><Skeleton animation="wave" variant="rounded" height={14} width={[200, 180, 220, 190, 210][i]} sx={{ borderRadius: '4px', mx: 'auto' }} /></TableCell>
                    <TableCell align="center"><Skeleton animation="wave" variant="rounded" height={14} width={88} sx={{ borderRadius: '4px', mx: 'auto' }} /></TableCell>
                    <TableCell align="center"><Skeleton animation="wave" variant="rounded" height={14} width={88} sx={{ borderRadius: '4px', mx: 'auto' }} /></TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', justifyContent: 'center', gap: '8px' }}>
                        <Skeleton animation="wave" variant="circular" width={18} height={18} />
                        <Skeleton animation="wave" variant="circular" width={18} height={18} />
                        <Skeleton animation="wave" variant="circular" width={18} height={18} />
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              : null}

            {!showFailedOnly && !isLoading && extractions?.length > 0 && documents?.length > 0
              ? extractions?.map((extraction: IExtraction) => {
                  const doc = documents.find(
                    (d) => getIdFromUrl(d.url) === getIdFromUrl(extraction.document)
                  );
                  return (
                    <ExtractionBatchExtractionListRow
                      key={extraction.id}
                      extraction={extraction}
                      document={doc}
                    />
                  );
                })
              : null}

            {showFailedOnly && !isLoading && failedCombined?.length > 0 && documents?.length > 0
              ? failedCombined?.map((extraction: IExtraction) => {
                  const doc = documents.find(
                    (d) => getIdFromUrl(d.url) === getIdFromUrl(extraction.document)
                  );
                  return (
                    <ExtractionBatchExtractionListRow
                      key={extraction.id}
                      extraction={extraction}
                      document={doc}
                    />
                  );
                })
              : null}

            {((!showFailedOnly && extractions?.length === 0) ||
              (showFailedOnly && failedCombined?.length === 0)) &&
            !isLoading &&
            !isFetching ? (
              <TableRow>
                <TableCell colSpan={9}>
                  <div style={{ display: 'flex', justifyContent: 'center', padding: '10px' }}>
                    No extractions found.
                  </div>
                </TableCell>
              </TableRow>
            ) : null}
          </TableBody>
        </Table>
      </Box>

      <DocumentDataEditStatus />
    </>
  );
}
export default ExtractionBatchDocumentList;
